<template>
  <div class="student-nav-menu">
    <!-- Overlay, drawer, trigger, and click-shield are teleported to <body>
         so they sit above page chrome and cannot inherit a containing-block
         (filter / transform) from #main-content. The wrapper re-establishes
         the .student-nav-menu ancestor that theme CSS selectors rely on
         (themes/default.css scopes nav rules under it). -->
    <Teleport to="body">
      <div class="student-nav-menu">
        <!-- Hamburger / trigger button -->
        <button
          ref="triggerBtn"
          @click="toggleMenu"
          class="hamburger-btn"
          :class="{ active: isMenuOpen }"
          :aria-expanded="isMenuOpen"
          aria-haspopup="menu"
          :aria-label="isMenuOpen ? 'Close menu' : 'Open menu'"
        >
          <X v-if="isMenuOpen" :size="22" />
          <Menu v-else :size="22" />
        </button>
        <!-- Overlay -->
        <transition name="nav-fade">
          <div
            v-if="isMenuOpen"
            class="nav-overlay"
            @mousedown.self="onBackdropPointerDown"
            @click.self="onBackdropClick($event, closeMenu)"
          ></div>
        </transition>

        <!-- Slide-out panel -->
        <div
          ref="panel"
          class="nav-panel"
          :class="{ open: isMenuOpen }"
          role="menu"
          aria-label="Student management menu"
          :inert="!isMenuOpen"
        >
          <div class="nav-header">
            <AppBrandHeader variant="nav" title-as="span" />
            <button @click="closeMenu" class="close-btn" aria-label="Close menu">
              <X :size="20" />
            </button>
          </div>

          <div class="nav-content">
            <div
              v-if="canAddStudents || canCreateEvaluations"
              class="create-strip"
              :class="{ one: !(canAddStudents && canCreateEvaluations) }"
              aria-label="Create"
            >
              <button
                v-if="canAddStudents"
                type="button"
                class="quick-action"
                :class="{ current: isCurrent('add-student') }"
                :aria-current="isCurrent('add-student') ? 'page' : undefined"
                role="menuitem"
                @click="handleAction('add-student')"
              >
                <UserPlus :size="16" />
                <span>New Student</span>
              </button>
              <button
                v-if="canCreateEvaluations"
                type="button"
                class="quick-action"
                :class="{ current: isCurrent('add-evaluation') }"
                :aria-current="isCurrent('add-evaluation') ? 'page' : undefined"
                role="menuitem"
                @click="handleAction('add-evaluation')"
              >
                <ClipboardList :size="16" />
                <span>New Evaluation</span>
              </button>
            </div>

            <div v-if="hasStudentItems" class="nav-section">
              <h4 class="nav-section-title">Students</h4>
              <div class="nav-buttons">
                <button
                  v-if="canViewStudentList"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('students') }"
                  :aria-current="isCurrent('students') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('students')"
                >
                  <GraduationCap :size="18" />
                  <span>Students</span>
                </button>
                <button
                  v-if="canViewMyCaseload"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('my-caseload') }"
                  :aria-current="isCurrent('my-caseload') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('my-caseload')"
                >
                  <GraduationCap :size="18" />
                  <span>My Caseload</span>
                </button>
                <button
                  v-if="canManageFeedback"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('feedback-manage') }"
                  :aria-current="isCurrent('feedback-manage') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('feedback-manage')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 14h6" />
                    <path d="M4 2h10" />
                    <rect x="4" y="18" width="16" height="4" rx="1" />
                    <rect x="4" y="6" width="16" height="4" rx="1" />
                  </svg>
                  <span>Feedback Forms</span>
                </button>
              </div>
            </div>

            <div v-if="hasMeetingsCaseloadItems" class="nav-section">
              <h4 class="nav-section-title">Meetings &amp; Caseload</h4>
              <div class="nav-buttons">
                <button
                  v-if="canViewMeetingCalendar"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('meeting-calendar') }"
                  :aria-current="isCurrent('meeting-calendar') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('meeting-calendar')"
                >
                  <CalendarDays :size="18" />
                  <span>Meeting Calendar</span>
                </button>
                <button
                  v-if="canViewCaseloadReport"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('caseload-report') }"
                  :aria-current="isCurrent('caseload-report') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('caseload-report')"
                >
                  <ClipboardList :size="18" />
                  <span>Caseload Report</span>
                </button>
              </div>
            </div>

            <div v-if="hasAideItems" class="nav-section">
              <h4 class="nav-section-title">Aides</h4>
              <div class="nav-buttons">
                <button
                  v-if="currentUser?.role === 'paraeducator'"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('aide-schedule') }"
                  :aria-current="isCurrent('aide-schedule') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('aide-schedule')"
                >
                  <CalendarDays :size="18" />
                  <span>My Schedule</span>
                </button>
                <button
                  v-if="canViewAideSchedule"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('master-schedule') }"
                  :aria-current="isCurrent('master-schedule') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('master-schedule')"
                >
                  <CalendarRange :size="18" />
                  <span>Aide Schedule</span>
                </button>
              </div>
            </div>

            <div v-if="hasTestingItems" class="nav-section">
              <h4 class="nav-section-title">Testing</h4>
              <div class="nav-buttons">
                <button
                  v-if="canViewTestingCalendar"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('testing-calendar') }"
                  :aria-current="isCurrent('testing-calendar') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('testing-calendar')"
                >
                  <CalendarDays :size="18" />
                  <span>Testing Calendar</span>
                </button>
                <button
                  v-if="canViewTestingAccommodations"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('testing-accommodations') }"
                  :aria-current="isCurrent('testing-accommodations') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('testing-accommodations')"
                >
                  <ClipboardList :size="18" />
                  <span>Testing Accommodations</span>
                </button>
                <button
                  v-if="canViewAttendanceLog"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('testing-attendance') }"
                  :aria-current="isCurrent('testing-attendance') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('testing-attendance')"
                >
                  <ClipboardCheck :size="18" />
                  <span>Attendance log</span>
                </button>
              </div>
            </div>

            <div class="nav-section">
              <h4 class="nav-section-title">Reports &amp; Resources</h4>
              <div class="nav-buttons">
                <button
                  v-if="canViewGenEdHours"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('gen-ed-hours') }"
                  :aria-current="isCurrent('gen-ed-hours') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('gen-ed-hours')"
                >
                  <BarChart3 :size="18" />
                  <span>Gen-Ed IEP Hours</span>
                </button>
                <button
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('reference-documents') }"
                  :aria-current="isCurrent('reference-documents') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('reference-documents')"
                >
                  <FolderOpen :size="18" />
                  <span>Reference Documents</span>
                </button>
              </div>
            </div>

            <div v-if="hasAdminItems" class="nav-section">
              <h4 class="nav-section-title">Admin</h4>
              <div class="nav-buttons">
                <button
                  v-if="canViewDistrict"
                  type="button"
                  class="nav-btn"
                  :class="{ current: isCurrent('district') }"
                  :aria-current="isCurrent('district') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('district')"
                >
                  <Building2 :size="18" />
                  <span>District Dashboard</span>
                </button>
                <button
                  v-if="canViewAdminPanel"
                  type="button"
                  class="nav-btn primary"
                  :class="{ current: isCurrent('admin') }"
                  :aria-current="isCurrent('admin') ? 'page' : undefined"
                  role="menuitem"
                  @click="handleAction('admin')"
                >
                  <Settings :size="18" />
                  <span>Admin Panel</span>
                </button>
              </div>
            </div>
          </div>

          <div class="account-section">
            <div class="account-card">
              <div class="account-avatar" aria-hidden="true">{{ userInitials }}</div>
              <div class="account-details">
                <strong class="account-name">{{
                  currentUser?.name || currentUser?.email || 'User'
                }}</strong>
                <span v-if="roleLabel" class="account-role">{{ roleLabel }}</span>
              </div>
            </div>
            <div class="nav-buttons">
              <label v-if="privacyDistrictEnabled" class="privacy-row" :title="privacyHint">
                <EyeOff :size="18" aria-hidden="true" />
                <span class="privacy-row-text">
                  <span class="privacy-row-label">Idle blur</span>
                  <span class="privacy-row-meta">{{ privacyBlurMinutes }} min</span>
                </span>
                <span class="privacy-switch" :class="{ on: privacyBlurOn }">
                  <input
                    v-model="privacyBlurOn"
                    type="checkbox"
                    role="switch"
                    :aria-checked="privacyBlurOn"
                    aria-label="Blur student data when idle"
                  />
                  <span class="privacy-switch-thumb" aria-hidden="true"></span>
                </span>
              </label>
              <button
                type="button"
                class="nav-btn"
                :class="{ current: isCurrent('help') }"
                :aria-current="isCurrent('help') ? 'page' : undefined"
                role="menuitem"
                @click="handleAction('help')"
              >
                <CircleHelp :size="18" />
                <span>Help</span>
              </button>
              <button
                type="button"
                class="nav-btn"
                role="menuitem"
                @click="handleAction('report-problem')"
              >
                <MessageSquareWarning :size="18" />
                <span>Report a Problem</span>
              </button>
              <button
                v-if="showInstallUi"
                type="button"
                class="nav-btn"
                role="menuitem"
                @click="onInstallClick"
              >
                <Smartphone :size="18" />
                <span>Install app</span>
              </button>
              <button
                type="button"
                class="nav-btn danger"
                role="menuitem"
                @click="handleAction('logout')"
              >
                <LogOut :size="18" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Swallows the leftover tap/ghost-click after the drawer closes so it
         cannot hit "Back to Students" (or anything else) on the page that
         just appeared underneath. -->
        <div v-if="clickShield" class="nav-click-shield" aria-hidden="true"></div>
      </div>
    </Teleport>

    <InstallAppSheet v-if="showInstallSheet" @close="showInstallSheet = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useBackdropDismiss } from '@/composables/useBackdropDismiss'
import { usePwaInstall } from '@/composables/usePwaInstall'
import InstallAppSheet from '@/components/pwa/InstallAppSheet.vue'
import { useRoute } from 'vue-router'
import {
  Menu,
  X,
  UserPlus,
  Settings,
  CalendarDays,
  CalendarRange,
  BarChart3,
  Building2,
  GraduationCap,
  FolderOpen,
  ClipboardList,
  ClipboardCheck,
  LogOut,
  CircleHelp,
  EyeOff,
  MessageSquareWarning,
  Smartphone
} from 'lucide-vue-next'
import {
  FEEDBACK_CREATORS,
  GEN_ED_HOURS_VIEWERS,
  DISTRICT_DASHBOARD_ROLES,
  MEETING_CALENDAR_ROLES,
  CASELOAD_REPORT_ROLES,
  STUDENT_ACCESS_ROLES
} from '@/config/roles'
import { canViewTestingCalendar as userCanViewTestingCalendar } from '@/config/testingCalendar'
import { EVAL_CREATE_ROLES, STUDENT_CREATE_ROLES } from '@/utils/studentEvaluation'
import { areEvaluationsEnabled } from '@/utils/environment'
import {
  canViewTestingAccommodationsPage as userCanViewTestingAccommodations,
  canViewTeacherAttendanceLog as userCanViewTeacherAttendanceLog
} from '@/config/testingAccommodations'
import { useNavVisibility } from '@/composables/useNavVisibility'
import { usePrivacyBlur } from '@/composables/usePrivacyBlur'
import AppBrandHeader from '@/components/brand/AppBrandHeader.vue'

const props = defineProps({
  currentUser: {
    type: Object,
    default: null
  },
  isAdmin: {
    type: Boolean,
    default: false
  },
  // False on district-level landers (/district, /my-caseload): school-scoped
  // sections (Create, Feedback, Aides, Reports, Testing) hide until
  // the user is working in an actual school context.
  schoolContext: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['action'])

const { loadNavVisibility, canSeeNav } = useNavVisibility()

// Privacy blur: district admin governs the baseline (app_settings/security);
// the user may opt out here. The toggle only appears when the district has it on.
const {
  isEnabled: privacyDistrictEnabled,
  userOptOut: privacyUserOptOut,
  blurMinutes: privacyBlurMinutes,
  setUserOptOut: setPrivacyOptOut
} = usePrivacyBlur()

const privacyBlurOn = computed({
  get: () => privacyDistrictEnabled.value && !privacyUserOptOut.value,
  set: (val) => setPrivacyOptOut(!val)
})

const privacyHint = computed(
  () =>
    `Hides student data after ${privacyBlurMinutes.value} min of inactivity. Move the mouse to reveal.`
)

// PWA install: phones/tablets only. Android fires the captured native prompt;
// iOS can't prompt programmatically, so it opens the instructions sheet.
const { showInstallUi, canPrompt, promptInstall } = usePwaInstall()
const showInstallSheet = ref(false)

async function onInstallClick() {
  closeMenu()
  if (canPrompt.value) {
    await promptInstall()
  } else {
    showInstallSheet.value = true
  }
}

const panel = ref(null)
const triggerBtn = ref(null)

onMounted(() => {
  loadNavVisibility()
  window.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown)
  resetBodyScrollLock()
  clearClickShield()
})

// The menu lives in AppLayout (outside router-view), so it survives route
// changes — close the drawer and release the body scroll lock on navigation.
const route = useRoute()
watch(
  () => route.fullPath,
  () => {
    closeMenu()
    resetBodyScrollLock()
  }
)

function resetBodyScrollLock() {
  document.body.style.overflow = ''
}

// Either admin flag opens the school-wide Aide Schedule page. The hamburger
// no longer lists individual aides — pick a person on that page.
const canViewAideSchedule = computed(
  () =>
    canSeeNav('master-schedule', props.currentUser?.role) ||
    canSeeNav('aide-schedules', props.currentUser?.role)
)

// New Student is full-read creators only (mirrors the students create rule in
// firestore.rules — plain/speech CMs lost create 2026-08-29 because they
// cannot check for duplicate SSIDs). New Evaluation is creators only
// (2026-08-30: chairs / psych / 504 coordinator / admins — CMs work evals
// assigned to them, they do not start them). Assigned work is the EV pill
// on Student Management.
const canAddStudents = computed(() => {
  return props.schoolContext && STUDENT_CREATE_ROLES.includes(props.currentUser?.role)
})

const canCreateEvaluations = computed(() => {
  return (
    areEvaluationsEnabled() &&
    props.schoolContext &&
    EVAL_CREATE_ROLES.includes(props.currentUser?.role)
  )
})

const canManageFeedback = computed(() => {
  return props.schoolContext && FEEDBACK_CREATORS.includes(props.currentUser?.role)
})

const canViewGenEdHours = computed(() => {
  return props.schoolContext && GEN_ED_HOURS_VIEWERS.includes(props.currentUser?.role)
})

// Printable per-case-manager roster — CASELOAD_REPORT_ROLES (chairs, admin
// tier, staff_* roles, school psych). Not a personal caseload page.
const canViewCaseloadReport = computed(() => {
  return props.schoolContext && CASELOAD_REPORT_ROLES.includes(props.currentUser?.role)
})

const canViewStudentList = computed(
  () => props.schoolContext && STUDENT_ACCESS_ROLES.includes(props.currentUser?.role)
)

// IEP due-date calendar — school-scoped like the other school tools; role set
// excludes admin_504 by design (see MEETING_CALENDAR_ROLES).
const canViewMeetingCalendar = computed(() => {
  return props.schoolContext && MEETING_CALENDAR_ROLES.includes(props.currentUser?.role)
})

// No district-tier hide here: once a district role has drilled into a school
// (schoolContext, gated by hasTestingItems below), the testing tools are as
// relevant to them as to a school admin. They could already reach the pages
// by URL — the menu just admits it now.
const canViewTestingCalendar = computed(() => userCanViewTestingCalendar(props.currentUser))

const canViewTestingAccommodations = computed(() =>
  userCanViewTestingAccommodations(props.currentUser)
)

const canViewAttendanceLog = computed(() => userCanViewTeacherAttendanceLog(props.currentUser))

const canViewDistrict = computed(() => {
  return DISTRICT_DASHBOARD_ROLES.includes(props.currentUser?.role)
})

// District case manager: allSchools-flagged case-manager roles get a cross-school
// caseload landing (not district-admin — no dashboard access).
const canViewMyCaseload = computed(
  () =>
    props.currentUser?.allSchools === true &&
    ['case_manager', 'case_manager_full', 'speech_case_manager'].includes(props.currentUser?.role)
)

const hasStudentItems = computed(
  () => canViewStudentList.value || canViewMyCaseload.value || canManageFeedback.value
)

const hasMeetingsCaseloadItems = computed(
  () => canViewMeetingCalendar.value || canViewCaseloadReport.value
)

// Per-section visibility so each header hides entirely when a role has no
// items in that group (avoids empty "Testing"/"Admin" headers). Both sections
// are school-scoped — they never appear without an active school context.
const hasAideItems = computed(
  () =>
    props.schoolContext && (props.currentUser?.role === 'paraeducator' || canViewAideSchedule.value)
)

const NAV_ROUTE_ACTIONS = {
  Students: 'students',
  MyCaseload: 'my-caseload',
  FeedbackManage: 'feedback-manage',
  MeetingCalendar: 'meeting-calendar',
  CaseloadReport: 'caseload-report',
  AideSchedule: 'aide-schedule',
  AideScheduleByAide: 'aide-schedule',
  AideScheduleMaster: 'master-schedule',
  TestingCalendar: 'testing-calendar',
  TestingAccommodations: 'testing-accommodations',
  TestingAttendance: 'testing-attendance',
  GenEdHours: 'gen-ed-hours',
  ReferenceDocuments: 'reference-documents',
  DistrictDashboard: 'district',
  Admin: 'admin',
  Help: 'help'
}

const currentAction = computed(() => {
  if (route.query.addStudent === '1') return 'add-student'
  if (route.query.addEvaluation === '1') return 'add-evaluation'
  return NAV_ROUTE_ACTIONS[route.name] || ''
})

function isCurrent(action) {
  return currentAction.value === action
}

const hasTestingItems = computed(
  () =>
    props.schoolContext &&
    (canViewTestingCalendar.value ||
      canViewTestingAccommodations.value ||
      canViewAttendanceLog.value)
)

// The admin panel manages one school at a time — only offer it with an active
// school context. district_admin reaches it by drilling into a school from the
// district dashboard (which sets the school), not from the district lander.
const canViewAdminPanel = computed(() => props.schoolContext && props.isAdmin)

const hasAdminItems = computed(() => canViewAdminPanel.value || canViewDistrict.value)

const userInitials = computed(() => {
  const source = props.currentUser?.name || props.currentUser?.email || 'U'
  const parts = source.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
})

// Presentational only: prettify the raw role string (e.g. case_manager_full).
const roleLabel = computed(() => {
  const role = props.currentUser?.role
  if (!role) return ''
  const pretty = role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  // A paraeducator granted the per-user testing flag is a "testing para" — make
  // that visible so they know why the testing pages appear for them.
  if (role === 'paraeducator' && props.currentUser?.testingAccess === true) {
    return `${pretty} (Testing)`
  }
  return pretty
})

const isMenuOpen = ref(false)
const clickShield = ref(false)
let clickShieldTimer = 0

const { onBackdropPointerDown, onBackdropClick } = useBackdropDismiss()

function clearClickShield() {
  if (clickShieldTimer) {
    clearTimeout(clickShieldTimer)
    clickShieldTimer = 0
  }
  clickShield.value = false
}

function armClickShield() {
  clickShield.value = true
  if (clickShieldTimer) clearTimeout(clickShieldTimer)
  // Covers the ~300ms iOS/Android ghost click after the overlay unmounts.
  clickShieldTimer = window.setTimeout(() => {
    clickShield.value = false
    clickShieldTimer = 0
  }, 400)
}

function toggleMenu() {
  if (isMenuOpen.value) {
    closeMenu()
    return
  }
  clearClickShield()
  isMenuOpen.value = true
}

function closeMenu() {
  if (!isMenuOpen.value) return
  isMenuOpen.value = false
  armClickShield()
}

function handleAction(action, payload) {
  emit('action', action, payload)
  closeMenu()
}

function handleKeydown(event) {
  if (event.key === 'Escape' && isMenuOpen.value) {
    closeMenu()
  }
}

// Lock body scroll while the drawer is open, and return focus to the trigger
// when it closes so keyboard users aren't stranded.
watch(isMenuOpen, async (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
  if (open) {
    await nextTick()
    panel.value?.querySelector('.close-btn')?.focus()
  } else if (triggerBtn.value?.isConnected) {
    triggerBtn.value.focus()
  }
})
</script>

<style scoped>
.student-nav-menu {
  position: relative;
}

/* Trigger button */
.hamburger-btn {
  position: fixed;
  top: 15px;
  right: 15px;
  z-index: 1001;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: var(--primary-color, #007bff);
  color: var(--text-inverse, #fff);
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition:
    background 0.2s ease,
    transform 0.2s ease;
  box-shadow: var(--shadow-lg, 0 4px 12px rgba(0, 123, 255, 0.3));
}

.hamburger-btn:hover {
  background: var(--primary-hover, #0056b3);
  transform: scale(1.05);
}

.hamburger-btn.active {
  background: var(--text-muted, #6c757d);
}

/* Overlay */
.nav-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 999;
  backdrop-filter: blur(2px);
}

.nav-fade-enter-active,
.nav-fade-leave-active {
  transition: opacity 0.25s ease;
}

.nav-fade-enter-from,
.nav-fade-leave-to {
  opacity: 0;
}

/* Panel */
.nav-panel {
  position: fixed;
  top: 0;
  right: -380px;
  width: 360px;
  max-width: 90vw;
  /* 100vh includes the area behind iOS Chrome's dynamic toolbar, which pushes
     the bottom menu items off-screen. dvh tracks the visible viewport instead. */
  height: 100vh;
  height: 100dvh;
  background: var(--bg-secondary, #fff);
  z-index: 1000;
  transition: right 0.3s ease;
  box-shadow: var(--shadow-xl, -4px 0 24px rgba(0, 0, 0, 0.18));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.nav-panel.open {
  right: 0;
}

.nav-panel:not(.open) {
  pointer-events: none;
}

.nav-click-shield {
  position: fixed;
  inset: 0;
  z-index: 1000;
}

/* Header */
.nav-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  background: var(--bg-tertiary, #f8f9fa);
  flex-shrink: 0;
}

.nav-header h3 {
  margin: 0;
  color: var(--header-text-color, #333);
  font-size: 1.1rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: var(--text-muted, #666);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: var(--bg-muted, #e9ecef);
}

/* Content */
.nav-content {
  padding: 12px 14px 16px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  flex: 1;
  min-height: 0;
}

.create-strip {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin: 0 0 14px;
}

.create-strip.one {
  grid-template-columns: 1fr;
}

.quick-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 4px 8px;
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 8px;
  background: rgba(25, 118, 210, 0.09);
  color: #0f4c8c;
  cursor: pointer;
  font: inherit;
  font-size: 0.81rem;
  font-weight: 650;
}

.quick-action :deep(svg) {
  color: #1976d2;
}

.quick-action.current,
.quick-action:hover {
  background: rgba(25, 118, 210, 0.16);
}

.quick-action:focus-visible {
  outline: 2px solid var(--primary-color, #1976d2);
  outline-offset: 1px;
}

.nav-section {
  margin-bottom: 16px;
}

.nav-section-title {
  margin: 0 6px 4px;
  padding: 0 4px 6px;
  border-bottom: 1px solid #eceff3;
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.nav-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.privacy-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  flex-direction: row;
  color: var(--text-secondary, #374151);
  transition: background 0.15s;
}

.privacy-row:hover {
  background: var(--bg-tertiary, #f1f3f5);
}

.privacy-row-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.privacy-row-label {
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.2;
}

.privacy-row-meta {
  font-size: 0.72rem;
  color: var(--text-muted, #8a929b);
  line-height: 1.2;
}

.privacy-switch {
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 20px;
}

.privacy-switch input {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  z-index: 1;
}

.privacy-switch-thumb {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.2s ease;
}

.privacy-switch-thumb::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

.privacy-switch.on .privacy-switch-thumb {
  background: var(--primary-color, #007bff);
}

.privacy-switch.on .privacy-switch-thumb::after {
  transform: translateX(16px);
}

.privacy-switch input:focus-visible + .privacy-switch-thumb {
  outline: 2px solid var(--primary-color, #007bff);
  outline-offset: 2px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 36px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: #1f2937;
  cursor: pointer;
  font-size: 0.94rem;
  font-weight: 500;
  transition:
    background 0.15s,
    color 0.15s;
  text-align: left;
  width: 100%;
}

.nav-btn :deep(svg) {
  color: #8b95a1;
  flex-shrink: 0;
}

.nav-btn:hover {
  background: #f4f6f8;
}

.nav-btn.current {
  background: rgba(25, 118, 210, 0.09);
  color: #0f4c8c;
  font-weight: 650;
}

.nav-btn.current :deep(svg) {
  color: #1976d2;
}

.nav-btn:focus-visible {
  outline: 2px solid var(--primary-color, #007bff);
  outline-offset: 1px;
}

.nav-btn.primary {
  background: transparent;
  border-color: var(--primary-color, #1b75d2);
  color: var(--primary-color, #1b75d2);
  font-weight: 600;
}

.nav-btn.primary :deep(svg) {
  color: var(--primary-color, #1b75d2);
}

.nav-btn.primary:hover {
  background: var(--primary-bg-subtle, rgba(27, 117, 210, 0.08));
  color: var(--primary-hover, #0056b3);
}

.nav-btn.primary.current {
  background: rgba(25, 118, 210, 0.09);
}

.nav-btn.danger {
  color: var(--error-color, #dc3545);
}

.nav-btn.danger:hover {
  background: rgba(220, 53, 69, 0.1);
}

/* Account stays pinned so Help / Logout never require a long scroll. */
.account-section {
  flex-shrink: 0;
  border-top: 1px solid var(--border-light, #e9ecef);
  padding: 10px 14px calc(14px + env(safe-area-inset-bottom, 0px));
  background: var(--bg-secondary, #fff);
}

.account-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 2px 12px;
}

.account-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary-color, #007bff);
  color: var(--text-inverse, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.account-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.account-name {
  color: var(--text-primary, #1f2937);
  font-size: 0.92rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-role {
  font-size: 0.78rem;
  color: var(--text-muted, #6c757d);
}

@media (pointer: coarse) {
  .nav-btn {
    min-height: 42px;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .nav-panel {
    width: 100vw;
    max-width: 100vw;
    right: -100vw;
  }
}
</style>
